---
title: "Withdrawals, Fees and Account Security on {{BRAND}}"
slug: withdrawals-fees-security
shortTitle: "Withdrawals & Security"
description: "How to withdraw from {{BRAND}} by crypto, bank wire or card refund, what must be in place first, which fees apply, and the security settings that protect your money."
level: beginner
category: trading
course: platform-guide
order: 7
topics: [security]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/withdrawals-fees-security.svg
faqs:
  - q: "Why can't I withdraw my full balance?"
    a: "Only free margin can be withdrawn. Margin used by open positions and reserved by pending orders stays locked, and losses on open positions reduce what is free."
  - q: "Can I use a newly added withdrawal address straight away?"
    a: "Yes. A new wallet address, bank account or card can be used as soon as you save it. We email you every time one is added, so you can react at once if it was not you."
  - q: "Can I withdraw USDT on the BEP20 network?"
    a: "No. BEP20 is available for deposits but not for withdrawals. Crypto withdrawals are offered in USDT on TRC20 or ERC20, and in BTC and ETH."
  - q: "How long does a withdrawal take?"
    a: "Requests move through pending, under review, processing and paid. Allow {{WITHDRAWAL_TIME}}, plus any time your bank or the blockchain needs to complete the transfer."
  - q: "Will {{BRAND}} staff ever ask for my password or 2FA code?"
    a: "No. Staff never need your password or 2FA codes. Treat anyone who asks for them, by email, chat or phone, as a scammer, and contact support through your account instead."
  - q: "What if I lose the phone with my authenticator app?"
    a: "That is what your 10 backup codes are for: each can be used in place of an authenticator code. Keep them offline and separate from your phone, and contact support from your account if you have lost both."
---

Getting money out matters as much as getting it in. On {{BRAND}}, withdrawals are requested from **Account → Withdraw** and go through a short review before they are paid. A handful of rules decide whether a request can go through: verification, a saved destination, your 2FA code and your free margin. Knowing them in advance avoids most surprises.

This guide covers the withdrawal steps, the costs to expect, and the security settings that keep your money yours.

<CTA label="Go to Withdraw" to="/account/withdraw" />

<KeyTakeaways>

- **Four things must be in place.** Approved verification, a saved destination, your 2FA code if 2FA is on, and enough free margin.
- **Only free margin can be withdrawn.** Open positions keep their margin locked, and withdrawing while trades are open lowers your margin level.
- **Check costs before you submit.** The Withdraw page shows the fee, the minimum and the processing time for each method.
- **Security settings protect withdrawals.** 2FA, a strong password and an email every time a new address is saved make it far harder for anyone else to move your money.

</KeyTakeaways>

## Before you withdraw: four checks

1. **Verification approved.** Your identity check must be complete. See [Account Setup, Verification and Deposits](/academy/article/account-setup-deposits) if it is not.
2. **A saved destination.** Withdrawals go to a destination you have saved, such as a wallet address or bank account. A new destination can be used as soon as you save it.
3. **Your 2FA code.** If two-factor authentication is on, you enter a code from your authenticator app.
4. **Enough free margin.** Only free margin can be withdrawn: equity minus the margin used by open positions and reserved by pending orders.

<Example title="How much can you withdraw? (illustrative numbers)">

Your balance is 3,000 USDT. One open position uses 800 USDT of margin and shows an open loss of 200 USDT.

- Equity: 3,000 − 200 = **2,800 USDT**.
- Free margin: 2,800 − 800 = **2,000 USDT**, the most you could withdraw.
- If you withdraw all 2,000, equity falls to 800 and the margin level becomes 800 ÷ 800 = **100%**.
- If you withdraw 1,000 instead, equity is 1,800 and the margin level is 1,800 ÷ 800 = **225%**.

</Example>

<Warning>

Withdrawing while positions are open removes the buffer that absorbs losses. A small move against you can then push the margin level down to {{MARGIN_WARNING_LEVEL}}, then {{MARGIN_CRITICAL_LEVEL}} and eventually {{STOP_OUT_LEVEL}}, where positions are closed automatically. Close or reduce positions first, or leave plenty of free margin behind.

</Warning>

## Withdrawal methods

<CompareTable caption="Ways to withdraw from {{BRAND}}">

| Method | Options | Worth knowing |
| --- | --- | --- |
| Crypto | USDT on TRC20, USDT on ERC20, BTC, ETH | BEP20 is for deposits only; match the network your receiving wallet expects |
| Bank wire | USD, EUR or GBP | Your bank or intermediary banks may charge their own fees |
| Card refund | Back to a card you used for a deposit | Only cards that funded the account can receive a refund |

</CompareTable>

## How to withdraw, step by step

<Steps>
<Step title="Open the Withdraw page">

Go to **Account → Withdraw**, choose **Withdraw** from the account menu, or open [the Withdraw page](/account/withdraw) directly.

</Step>
<Step title="Choose a method">

Pick crypto, bank wire or card refund, and for crypto the asset and network.

</Step>
<Step title="Choose a saved destination">

Select a wallet address, bank account or card you have already saved. If you add a new one now, you can use it straight away.

</Step>
<Step title="Enter the amount">

Enter an amount of at least {{MIN_WITHDRAWAL}} and no more than your free margin. Before you continue, check the fee for your method.

</Step>
<Step title="Confirm with 2FA and submit">

Enter the code from your authenticator app if 2FA is on, then submit the request.

</Step>
</Steps>

<Screenshot name="withdraw-crypto-form" alt="The crypto withdrawal form with coin and network, a saved address, the amount, the network fee and the authenticator code field" caption="Account → Withdraw: choose the coin, network and saved address, enter the amount and confirm with your authenticator code." />

{/* TODO: verify with platform — exact field labels on the Withdraw form, where destinations are saved, and whether the fee is taken from the amount or added to it. */}

For crypto, copy your receiving address from your own wallet rather than typing it, and check the network matches. A withdrawal sent to an address on the wrong network can be lost.

## Tracking your request

Each request moves through four statuses: **pending**, **under review**, **processing** and **paid**. Allow {{WITHDRAWAL_TIME}}, plus any time the blockchain or your bank needs once the payment has left.

<Screenshot name="withdrawal-status-list" alt="A list of withdrawal requests with amount, method and status from pending to paid" caption="Follow each request from pending to paid." />

{/* TODO: verify with platform — where the withdrawal history is shown (Withdraw page, Wallet or Portfolio → Money movements). */}

If you have an active bonus, making a withdrawal ends it. Check the bonus terms before you request one.

{/* TODO: verify with platform — that a withdrawal ends an active bonus, and how this is shown to the client. */}

## If a withdrawal is held up

Most delays have a simple cause. Check these before contacting support:

- **Verification.** A document may have expired or been rejected; look at the **KYC** tab under **Personal Information**.
- **The destination.** The saved address or account was removed, or its coin and network are no longer offered for withdrawals.
- **The amount.** It is below the minimum, or above your free margin because open positions have moved against you since you started.
- **The card.** Card refunds can only go back to a card that was used for a deposit.
- **The network.** For crypto, make sure the network you chose is one your receiving wallet supports.

If none of these applies, contact support from **Account → Support** with the request's amount, method and date. Support will never need your password or 2FA codes to help.

## Fees: what to expect

Two kinds of cost matter here. On the way out you pay {{WITHDRAWAL_FEE}}, and each request must be at least {{MIN_WITHDRAWAL}}; both can differ by method. Banks and blockchains can add their own charges on top, so the amount that arrives may be a little lower than the amount you requested.

While you trade, the costs are the spread (the gap between Bid and Ask), the trade fee ({{TRADE_FEE}}) and the daily swap on positions held past {{SWAP_HOUR}}. The **Fees** and **Swap** columns in **Closed Positions** show what each trade actually cost. [Funding, Swaps and Trading Costs](/academy/article/trading-costs-swaps-fees) explains each one with examples.

## Securing your account

Your security settings protect withdrawals as much as logins. Start with two-factor authentication (2FA): a second code from an app on your phone, needed as well as your password.

<Steps>
<Step title="Open Security">

Go to **Account → Personal information → Security**.

</Step>
<Step title="Scan the QR code">

Scan the QR code with an authenticator app on your phone.

</Step>
<Step title="Enter the code to confirm">

Type the code the app shows to switch 2FA on.

</Step>
<Step title="Save your backup codes">

Store the 10 backup codes somewhere safe and offline. Each can replace an app code if you lose your phone.

</Step>
</Steps>

<Screenshot name="security-2fa-setup" alt="The Security tab with the authenticator-app QR code, setup key and code field" caption="Turn on 2FA under Account → Personal information → Security." />

A few more protections are worth knowing:

- **Passwords** need at least 8 characters with an upper-case letter, a lower-case letter and a digit. Changing your password signs out your other sessions.
- **Password reset links** expire after 30 minutes.
- **New sign-in alerts** tell you when someone signs in from a browser {{BRAND}} has not seen before. If it was not you, change your password straight away.

<ProTip>

Bookmark the {{BRAND}} login page and always sign in from the bookmark. Phishing sites copy real login pages closely, and a bookmark is a simple way to avoid fake links in emails and messages.

</ProTip>

<Warning>

{{BRAND}} staff will never ask for your password or 2FA codes. Anyone who does, whatever they claim, is trying to take over your account. [Security Basics](/academy/article/security-basics) covers common scams in more detail.

</Warning>

## Conclusion

Withdrawing from {{BRAND}} is straightforward once four things are in place: approved verification, a saved destination, your 2FA code and enough free margin. Check the fee, minimum and processing time on the Withdraw page, follow the request from pending to paid, and leave a margin buffer if positions are still open. Turn on 2FA, keep your backup codes offline, and never share your codes or password with anyone.
